import { Piano } from "../piano";
import {
  detectTransport,
  dispatchMidiMessage,
  gestureInstant,
  type MidiActivityListener,
  type MidiDeviceInfo,
  type MidiInputAdapter,
  type MidiInputChangeListener,
} from "./midi-shared";

export type { MidiActivityListener, MidiDeviceInfo, MidiDeviceTransport, MidiInputChangeListener } from "./midi-shared";

export class WebMidiInput implements MidiInputAdapter {
  private access?: MIDIAccess;
  /** Map from `MIDIInput.id` → live `MIDIInput`. */
  private inputs = new Map<string, MIDIInput>();
  /** Per-device enabled flag. Defaults to `true` on first sight. */
  private enabledById = new Map<string, boolean>();
  /** Optional active-device id, pure cosmetic for now. */
  private activeId: string | null = null;

  private changeListeners = new Set<MidiInputChangeListener>();
  private activityListeners = new Set<MidiActivityListener>();

  constructor(private readonly piano: Piano) {}

  async attach(): Promise<void> {
    if (!("requestMIDIAccess" in navigator)) {
      throw new Error("Web MIDI API not supported");
    }

    this.onMidiMessage = this.onMidiMessage.bind(this);
    this.onStateChange = this.onStateChange.bind(this);

    this.access = await navigator.requestMIDIAccess();
    this.access.onstatechange = this.onStateChange;

    for (const input of this.access.inputs.values()) {
      this.registerInput(input);
    }

    this.emitChange();
  }

  detach(): void {
    if (this.access) {
      this.access.onstatechange = null;
    }

    for (const input of this.inputs.values()) {
      input.onmidimessage = null;
    }

    this.inputs.clear();
    this.enabledById.clear();
    this.activeId = null;
    this.piano.allNotesOff();
    this.emitChange();
  }

  /** Re-walk `access.inputs`, useful as a "rescan" CTA backing call. */
  rescan(): void {
    if (!this.access) {
      return;
    }

    const seen = new Set<string>();

    for (const input of this.access.inputs.values()) {
      seen.add(input.id);
      this.registerInput(input);
    }

    for (const id of Array.from(this.inputs.keys())) {
      if (!seen.has(id)) {
        this.inputs.delete(id);
      }
    }

    this.emitChange();
  }

  getDevices(): readonly MidiDeviceInfo[] {
    return Array.from(this.inputs.values()).map((input) => ({
      id: input.id,
      name: input.name ?? "Périphérique MIDI",
      manufacturer: input.manufacturer ?? "",
      transport: detectTransport(input.name),
      connected: input.state === "connected",
      enabled: this.enabledById.get(input.id) ?? true,
    }));
  }

  getActiveDeviceId(): string | null {
    return this.activeId;
  }

  setActiveDevice(id: string | null): void {
    if (this.activeId === id) {
      return;
    }

    this.activeId = id;
    this.emitChange();
  }

  setEnabled(id: string, enabled: boolean): void {
    const current = this.enabledById.get(id) ?? true;

    if (current === enabled) {
      return;
    }

    this.enabledById.set(id, enabled);
    const input = this.inputs.get(id);

    if (input) {
      input.onmidimessage = enabled ? this.onMidiMessage : null;
    }

    this.emitChange();
  }

  onChange(listener: MidiInputChangeListener): () => void {
    this.changeListeners.add(listener);

    return () => {
      this.changeListeners.delete(listener);
    };
  }

  onActivity(listener: MidiActivityListener): () => void {
    this.activityListeners.add(listener);

    return () => {
      this.activityListeners.delete(listener);
    };
  }

  private emitChange(): void {
    for (const listener of this.changeListeners) {
      listener();
    }
  }

  private registerInput(input: MIDIInput): void {
    this.inputs.set(input.id, input);

    if (!this.enabledById.has(input.id)) {
      this.enabledById.set(input.id, true);
    }

    const enabled = this.enabledById.get(input.id) ?? true;
    input.onmidimessage = enabled ? this.onMidiMessage : null;
  }

  private onStateChange(event: MIDIConnectionEvent): void {
    const port = event.port;

    if (!port || port.type !== "input") {
      return;
    }

    const input = port as MIDIInput;

    if (input.state === "connected") {
      this.registerInput(input);
    } else {
      // Disconnected: keep the user-set `enabled` flag so it survives a
      // reconnect, but drop the live binding.
      input.onmidimessage = null;
      this.inputs.delete(input.id);
    }

    this.emitChange();
  }

  private onMidiMessage(e: MIDIMessageEvent): void {
    if (!e.data) {
      return;
    }

    const sourceId = (e.target as MIDIInput | null)?.id ?? "";
    // L'instant de réception plutôt que celui de ce gestionnaire : voir
    // `gestureInstant`.
    const result = dispatchMidiMessage(this.piano, e.data, gestureInstant(e.timeStamp));

    if (result?.kind === "noteOn") {
      this.emitActivity({ id: sourceId, midi: result.midi, velocity: result.velocity });
    }
  }

  private emitActivity(payload: { id: string; midi: number; velocity: number }): void {
    for (const listener of this.activityListeners) {
      listener(payload);
    }
  }
}
